import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import RestaurantItem from '../components/RestaurantItem';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import NavigationBar from '../components/NavigationBar';
import BottomBanner from '../components/BottomBanner';
import ServiceClosingModal from '../components/ServiceClosingModal';
import { useCurationDetail } from '../hooks/useCurationDetail';
import { convertCurationStoreToRestaurant } from '../utils/curationUtils';
import { getCurrentLanguage } from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';
import { SupportedCurrency, getCurrencyFromLanguage } from '../utils/currencyUtils';
import { useServiceClosingNotification } from '../hooks/useOperationTime';

const CurationDetail: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { t } = useTranslation();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    getCurrencyFromLanguage(currentLang)
  );
  
  // 운영시간 체크 훅 추가
  const { 
    shouldShowClosingModal, 
    checkClosingStatus, 
    dismissClosingModal 
  } = useServiceClosingNotification();

  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLang(newLang);
      setSelectedCurrency(getCurrencyFromLanguage(newLang));
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);

  // 운영시간 체크 (페이지 로드 시 및 주기적으로)
  useEffect(() => {
    // 초기 체크
    checkClosingStatus();
    
    // 5분마다 운영시간 체크
    const interval = setInterval(() => {
      checkClosingStatus();
    }, 5 * 60 * 1000); // 5분
    
    return () => clearInterval(interval);
  }, [checkClosingStatus]);
  
  // API 요청
  const { stores, loading, error, refetch } = useCurationDetail(categoryId || '');
  
  // CurationStore를 Restaurant로 변환
  const restaurants = useMemo(() => {
    return stores.map(store => convertCurationStoreToRestaurant(store));
  }, [stores]);
  
  // 카테고리별 제목 가져오기
  const getCategoryTitle = useCallback((category: string): string => {
    const translationKey = `curation.category.${category}`;
    return t(translationKey) || category;
  }, [t]);
  
  // 표시할 타이틀 결정
  const displayTitle = useMemo((): string => {
    if (categoryId) {
      return getCategoryTitle(categoryId);
    }
    return t('browse.restaurants');
  }, [categoryId, t, getCategoryTitle]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <BackHeader title={displayTitle} />
      
      <main className="p-4 pb-20">
        {/* 콘텐츠 영역: 로딩/에러/빈 결과/목록 중 하나만 표시 */}
        {loading ? (
          <LoadingState 
            message={t('curation.loading')} 
            id={`curation-${categoryId || 'unknown'}`}
          />
        ) : error ? (
          <ErrorState 
            message={t('curation.error')} 
            onRetry={refetch} 
            subtitle={t('error.connectionIssue')}
          />
        ) : restaurants.length === 0 ? (
          <EmptyState 
            message={t('status.empty.restaurants')} 
            subtitle={t('empty.changeLocationOrCategory')}
            actionText={t('action.refresh')}
            onAction={refetch}
            hideIcon={true}
          />
        ) : (
          <div>
            {restaurants.map((restaurant) => (
              <RestaurantItem 
                key={restaurant.id} 
                restaurant={restaurant} 
                currency={selectedCurrency}
                isFromCategory={true}
              />
            ))}
          </div>
        )}
      </main>
      
      {/* 네비게이션 바 추가 */}
      <BottomBanner />
      <NavigationBar />
      
      {/* 운영 종료 30분 전 알림 모달 */}
      <ServiceClosingModal 
        isOpen={shouldShowClosingModal}
        onClose={dismissClosingModal}
      />
    </div>
  );
};

export default CurationDetail; 